import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { LogOut, Clock, ListChecks, Target, FileText, Activity, CalendarRange, LayoutDashboard, Palmtree, Settings, Users, CalendarClock, ChevronDown, KeyRound, UserCog, Loader2, Handshake, Building2, Calculator, BedDouble, Lock, ClipboardCheck } from "lucide-react";

function AccountDialog({ dark }) {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingName, setSavingName] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [flags, setFlags] = useState(null);

  useEffect(() => {
    if (open && user?.role !== "admin") {
      api.get("/settings/feature-access").then((r) => setFlags(r.data.flags)).catch(() => {});
    }
  }, [open, user]);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setPw({ current: "", next: "", confirm: "" });
    }
  }, [open, user]);

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await api.patch("/auth/profile", { name });
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingName(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error("New passwords do not match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className={`hidden lg:flex items-center gap-1.5 text-sm rounded-lg px-2 py-1 transition-colors ${dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"}`} data-testid="account-btn">
          <UserCog className="h-4 w-4" /> {user?.name}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>My account</DialogTitle>
          <DialogDescription>Update your display name or change your login password.</DialogDescription>
        </DialogHeader>
        {user?.role !== "admin" && flags && !flags.staff_update_profile ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="account-disabled-notice">
            Self-service profile &amp; password updates are disabled by your admin. Contact HR to change your account.
          </p>
        ) : (
          <>
        <form onSubmit={saveName} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Login email</Label>
            <Input value={user?.email || ""} disabled className="opacity-60" data-testid="account-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Display name</Label>
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="account-name-input" />
              <Button type="submit" disabled={savingName || name.trim() === (user?.name || "")} data-testid="account-save-name">
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </form>
        <div className="border-t border-slate-200 pt-4">
          <p className="text-sm font-medium mb-3">Change password</p>
          <form onSubmit={changePw} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Current password</Label>
              <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required data-testid="account-current-password" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>New password</Label>
                <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required minLength={6} data-testid="account-new-password" />
              </div>
              <div className="space-y-1.5">
                <Label>Confirm new</Label>
                <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required minLength={6} data-testid="account-confirm-password" />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={savingPw} data-testid="account-change-password-btn">
              {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}
            </Button>
          </form>
        </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const STAFF_LINKS = [
  { to: "/staff/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/staff/tasks", label: "Tasks", icon: ListChecks },
];

const STAFF_ATTENDANCE_LINKS = [
  { to: "/staff", label: "Attendance", icon: Clock },
  { to: "/staff/schedule", label: "Schedule", icon: CalendarClock },
  { to: "/staff/leave", label: "Leave", icon: Palmtree },
];

const STAFF_CHANNEL_LINKS = [
  { to: "/staff/channels", label: "Channels", icon: KeyRound },
  { to: "/staff/channel-checklist", label: "Check list", icon: ClipboardCheck },
  { to: "/staff/rate-simulator", label: "Rate Simulator", icon: Calculator, flagKey: "staff_rate_simulator" },
];

const ADMIN_LINKS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin", label: "Monitoring", icon: Activity },
  { to: "/admin/tasks", label: "Tasks", icon: ListChecks },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const ADMIN_CHANNEL_LINKS = [
  { to: "/admin/channels", label: "Channels", icon: KeyRound },
  { to: "/admin/channel-checklist", label: "Check list", icon: ClipboardCheck },
  { to: "/admin/rate-simulator", label: "Rate Simulator", icon: Calculator },
];

const ADMIN_PERF_LINKS = [
  { to: "/admin/performance", label: "Monthly", icon: Target },
  { to: "/admin/yearly", label: "Yearly", icon: CalendarRange },
  { to: "/admin/reports", label: "Report", icon: FileText },
  { to: "/admin/reservation-report", label: "Reservation", icon: BedDouble },
];

const STAFF_PERF_LINKS = [
  { to: "/staff/performance", label: "Monthly", icon: Target },
  { to: "/staff/yearly", label: "Yearly", icon: CalendarRange },
  { to: "/staff/reports", label: "Report", icon: FileText },
  { to: "/staff/reservation-report", label: "Reservation", icon: BedDouble, flagKey: "staff_reservation_report" },
];

const HR_ADMIN_LINKS = [
  { to: "/admin/leave", label: "Leave", icon: Palmtree },
  { to: "/admin/schedule", label: "Schedule Kerja", icon: CalendarClock },
];

const PARTNER_LINKS = [
  { to: "/admin/partners", label: "New", icon: Handshake },
  { to: "/admin/partner-properties", label: "Property", icon: Building2 },
];

export function AppNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [flags, setFlags] = useState(null);
  useEffect(() => {
    if (!isAdmin) {
      api.get("/settings/feature-access").then((r) => setFlags(r.data.flags)).catch(() => {});
    }
  }, [isAdmin]);
  const links = isAdmin ? ADMIN_LINKS : STAFF_LINKS;
  const dark = isAdmin;
  const perfLinks = isAdmin ? ADMIN_PERF_LINKS : STAFF_PERF_LINKS;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className={`sticky top-0 z-30 backdrop-blur-md border-b ${dark ? "bg-[#1F1B16]/80 border-slate-800" : "bg-white/80 border-slate-200"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <div className="h-9 w-9 rounded-xl bg-[#1F1B16] ring-1 ring-black/5 flex items-center justify-center overflow-hidden p-1.5">
            <img src="/logo-mark.png" alt="BedsolvingOps" className="h-full w-full object-contain" />
          </div>
          <div className="hidden sm:block">
            <div className={`font-display font-bold leading-none ${dark ? "text-white" : "text-slate-900"}`}>BedsolvingOps</div>
            <div className={`text-[10px] ${dark ? "text-slate-400" : "text-slate-500"}`}>Seamless Workflow &amp; Transparent Yield</div>
          </div>
        </div>

        <nav className="flex items-center gap-1 overflow-x-auto flex-1 justify-center">
          {links.map((l) => {
            const active = location.pathname === l.to;
            const locked = !isAdmin && l.flagKey && flags && !flags[l.flagKey];
            if (locked) {
              return (
                <button
                  key={l.to}
                  onClick={() => toast.error("Fitur ini dikunci oleh admin")}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  title="Dikunci oleh admin"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors text-slate-600 cursor-not-allowed"
                >
                  <l.icon className="h-4 w-4" />
                  <span className="hidden md:inline">{l.label}</span>
                  <Lock className="h-3 w-3" />
                </button>
              );
            }
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? dark ? "bg-slate-800 text-white" : "bg-indigo-50 text-indigo-700"
                    : dark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <l.icon className="h-4 w-4" />
                <span className="hidden md:inline">{l.label}</span>
              </Link>
            );
          })}
          {!isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-attendance"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    STAFF_ATTENDANCE_LINKS.some((l) => l.to === location.pathname)
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  <span className="hidden md:inline">Attendance</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {STAFF_ATTENDANCE_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link to={l.to} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-${l.label.toLowerCase()}`}>
                      <l.icon className="h-4 w-4" /> {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!isAdmin && flags && (flags.staff_view_channels || flags.staff_rate_simulator) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-channels"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    STAFF_CHANNEL_LINKS.some((l) => l.to === location.pathname)
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <KeyRound className="h-4 w-4" />
                  <span className="hidden md:inline">Channels</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {STAFF_CHANNEL_LINKS.filter((l) => l.flagKey !== undefined || flags.staff_view_channels).map((l) => {
                  const locked = l.flagKey && flags && !flags[l.flagKey];
                  if (locked) {
                    return (
                      <DropdownMenuItem
                        key={l.to}
                        onSelect={(e) => { e.preventDefault(); toast.error("Fitur ini dikunci oleh admin"); }}
                        className="flex items-center gap-2 cursor-not-allowed text-slate-400"
                        data-testid={`nav-staff-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <l.icon className="h-4 w-4" /> {l.label} <Lock className="h-3 w-3 ml-auto" />
                      </DropdownMenuItem>
                    );
                  }
                  return (
                    <DropdownMenuItem key={l.to} asChild>
                      <Link to={l.to} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-staff-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <l.icon className="h-4 w-4" /> {l.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-channels-menu"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    ADMIN_CHANNEL_LINKS.some((l) => l.to === location.pathname)
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                  }`}
                >
                  <KeyRound className="h-4 w-4" />
                  <span className="hidden md:inline">Channels</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-200">
                {ADMIN_CHANNEL_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild className="focus:bg-slate-800 focus:text-white">
                    <Link to={l.to} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-ch-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <l.icon className="h-4 w-4" /> {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-performance"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  perfLinks.some((l) => l.to === location.pathname)
                    ? dark ? "bg-slate-800 text-white" : "bg-indigo-50 text-indigo-700"
                    : dark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <Target className="h-4 w-4" />
                <span className="hidden md:inline">Performance</span>
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={dark ? "bg-slate-900 border-slate-700 text-slate-200" : ""}>
              {perfLinks.map((l) => {
                const locked = !isAdmin && l.flagKey && flags && !flags[l.flagKey];
                if (locked) {
                  return (
                    <DropdownMenuItem
                      key={l.to}
                      onSelect={(e) => { e.preventDefault(); toast.error("Fitur ini dikunci oleh admin"); }}
                      className="flex items-center gap-2 cursor-not-allowed text-slate-400"
                      data-testid={`nav-perf-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <l.icon className="h-4 w-4" /> {l.label} <Lock className="h-3 w-3 ml-auto" />
                    </DropdownMenuItem>
                  );
                }
                return (
                  <DropdownMenuItem key={l.to} asChild className={dark ? "focus:bg-slate-800 focus:text-white" : ""}>
                    <Link to={l.to} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-perf-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <l.icon className="h-4 w-4" /> {l.label}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-hr-admin"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    HR_ADMIN_LINKS.some((l) => l.to === location.pathname)
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                  }`}
                >
                  <Users className="h-4 w-4" />
                  <span className="hidden md:inline">HR Admin</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-200">
                {HR_ADMIN_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild className="focus:bg-slate-800 focus:text-white">
                    <Link to={l.to} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <l.icon className="h-4 w-4" /> {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-partner"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    PARTNER_LINKS.some((l) => l.to === location.pathname)
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                  }`}
                >
                  <Handshake className="h-4 w-4" />
                  <span className="hidden md:inline">Partner</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-200">
                {PARTNER_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} asChild className="focus:bg-slate-800 focus:text-white">
                    <Link to={l.to} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-partner-${l.label.toLowerCase()}`}>
                      <l.icon className="h-4 w-4" /> {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        <div className="flex items-center gap-3 flex-shrink-0">
          <AccountDialog dark={dark} />
          <span className={`lg:hidden text-sm ${dark ? "text-slate-300" : "text-slate-700"}`}>{user?.name}</span>
          <Button variant="outline" size="sm" onClick={doLogout} className={dark ? "bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" : ""} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
